import { nextTick } from 'vue'

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

const DEFAULT_ACTION_SELECTORS = [
  '[data-modal-default]',
  'button[type="submit"]',
  'input[type="submit"]',
  '.btn-default-blue',
  '.button_pay_for_tarif',
  '.btn-default-selected-blue-border',
  '.btn-primary',
].join(',')

const TEXT_INPUT_SELECTOR = [
  'input[type="text"]:not([disabled]):not([readonly])',
  'input:not([type]):not([disabled]):not([readonly])',
  'textarea:not([disabled]):not([readonly])',
].join(',')

// Opt-out for controls that stay Tab-reachable but must never receive this
// directive's auto-focus-on-open — e.g. a TariffLockBadge trigger, often the
// only non-disabled control in a fully tariff-locked form.
const AUTOFOCUS_SKIP_SELECTOR = '[data-modal-autofocus-skip]'

const isVisible = (element) =>
  !!(element && (element.offsetWidth || element.offsetHeight || element.getClientRects().length))

const getFocusable = (container, { skipAutoFocusSkip = false } = {}) => {
  const elements = Array.from(container.querySelectorAll(FOCUSABLE_SELECTORS)).filter(isVisible)
  return skipAutoFocusSkip ? elements.filter((el) => !el.matches(AUTOFOCUS_SKIP_SELECTOR)) : elements
}

const findDefaultAction = (container, customSelector) => {
  if (customSelector) {
    const custom = container.querySelector(customSelector)
    if (custom && isVisible(custom) && !custom.disabled) {
      return custom
    }
  }

  const candidate = container.querySelector(DEFAULT_ACTION_SELECTORS)
  if (candidate && isVisible(candidate) && !candidate.disabled) {
    return candidate
  }

  return null
}

const shouldIgnoreEnter = (target) => {
  if (!target) return false
  const tag = target.tagName

  if (tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  if (tag === 'BUTTON' || tag === 'A') return true

  if (tag === 'INPUT') {
    const type = (target.getAttribute('type') || 'text').toLowerCase()
    return ['button', 'submit', 'checkbox', 'radio', 'file'].includes(type)
  }

  return false
}

const focusInitial = (container, state) => {
  if (!isVisible(container)) return

  const currentFocus = document.activeElement
  if (container.contains(currentFocus)) return

  const textInput = container.querySelector(TEXT_INPUT_SELECTOR)
  if (textInput && isVisible(textInput)) {
    textInput.focus()
    return
  }

  const defaultAction = findDefaultAction(container, state.defaultActionSelector)
  if (defaultAction) {
    defaultAction.focus()
    return
  }

  const focusable = getFocusable(container, { skipAutoFocusSkip: true })
  if (focusable.length) {
    focusable[0].focus()
    return
  }

  container.setAttribute('tabindex', container.getAttribute('tabindex') || '-1')
  container.focus()
}

const focusTrap = (event, container) => {
  const focusable = getFocusable(container)
  if (!focusable.length) {
    event.preventDefault()
    return
  }

  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  const active = document.activeElement

  if (event.shiftKey) {
    if (active === first || !container.contains(active)) {
      event.preventDefault()
      last.focus()
    }
  } else if (active === last || !container.contains(active)) {
    event.preventDefault()
    first.focus()
  }
}

const requestClose = (state, container) => {
  if (typeof state.onClose === 'function') {
    state.onClose()
    return
  }

  const closeEl = container.querySelector(
    '[data-modal-close], .a-q-m_close, .payment_popup_close, .close-btn'
  )
  if (closeEl) {
    closeEl.click()
    return
  }

  const overlay = container.querySelector('.modal-overlay')
  if (overlay) {
    overlay.click()
  }
}

const normalizeConfig = (value) => {
  if (typeof value === 'boolean') {
    return { isOpen: value }
  }
  return value || {}
}

const hasExplicitOpen = (config) =>
  Object.prototype.hasOwnProperty.call(config, 'isOpen')

export default {
  mounted(el, binding) {
    const config = normalizeConfig(binding.value)

    const state = {
      hasExplicitOpen: hasExplicitOpen(config),
      isOpen: hasExplicitOpen(config) ? !!config.isOpen : isVisible(el),
      onClose: config.onClose,
      defaultActionSelector: config.defaultActionSelector,
      wasVisible: isVisible(el),
    }

    const handler = (event) => {
      if (!state.isOpen) return

      if (event.key === 'Escape') {
        event.preventDefault()
        requestClose(state, el)
        return
      }

      if (event.key === 'Tab') {
        focusTrap(event, el)
        return
      }

      if (event.key === 'Enter' && !shouldIgnoreEnter(event.target)) {
        const defaultAction = findDefaultAction(el, state.defaultActionSelector)
        if (defaultAction) {
          event.preventDefault()
          defaultAction.click()
        }
      }
    }

    state.handler = handler
    el.__modalA11y = state
    el.addEventListener('keydown', handler)

    if (state.isOpen) {
      nextTick(() => focusInitial(el, state))
    }
  },
  updated(el, binding) {
    const state = el.__modalA11y
    if (!state) return

    const config = normalizeConfig(binding.value)
    const wasOpen = state.isOpen
    const wasVisible = state.wasVisible
    const explicitOpen = hasExplicitOpen(config)
    const isVisibleNow = isVisible(el)

    state.hasExplicitOpen = explicitOpen
    state.isOpen = explicitOpen ? !!config.isOpen : isVisibleNow
    state.onClose = config.onClose
    state.defaultActionSelector = config.defaultActionSelector
    state.wasVisible = isVisibleNow

    if ((!wasOpen && state.isOpen) || (!explicitOpen && !wasVisible && isVisibleNow)) {
      nextTick(() => focusInitial(el, state))
    }
  },
  beforeUnmount(el) {
    const state = el.__modalA11y
    if (!state) return

    el.removeEventListener('keydown', state.handler)
    delete el.__modalA11y
  },
}
